import { usePremiumInfoUrl } from '@src/utils/premiumReturn';
import { isDotCom } from '@src/site';
import { Link } from '@src/components/Link/Link';
import { PageHeading } from '@src/components/PageHeading/PageHeading';
import { useNavigate } from 'react-router-dom';
import { ExchangeGrid } from '@src/components/LandingLayout/LandingLayout';
import { HomeArtwork } from '@src/components/HomeArtwork/HomeArtwork';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  Button,
  Card,
  Columns,
  Container,
  PageContent,
  Panel,
  Expandable,
  ExpandableGroup,
  ExchangeCard,
  Prose,
  Shell,
  Stack,
  Typography,
} from '@src/components';
import type { CardTone } from '@src/components';
import { EXCHANGE_CARDS } from '@src/content/exchanges';
import { landingConfigFor } from '@src/pages/LandingPage/configs';

import type {
  LandingCopy,
  LandingListItem,
  LandingPageConfig,
  LandingSection,
} from '@src/pages/LandingPage/types';

const STEP_TONES: CardTone[] = ['support', 'accent', 'info', 'success', 'warning', 'neutral'];

function boxLink(config: LandingPageConfig): string {
  if (config.buttonLink) {
    return config.buttonLink;
  }
  if (config.namePreset) {
    return `/box/new?name=${encodeURIComponent(config.namePreset)}`;
  }
  return '/box/new';
}

function isListItem(item: string | LandingListItem): item is LandingListItem {
  return typeof item === 'object' && item !== null && 'title' in item;
}

function LandingCta({
  config,
  label,
  testId,
}: {
  config: LandingPageConfig;
  label: string;
  testId: string;
}) {
  const navigate = useNavigate();
  return (
    <Container width="narrow">
      <Button variant="primary" size="large" fullWidth testId={testId} onClick={() => navigate(boxLink(config))}>
        {label}
      </Button>
    </Container>
  );
}

function LandingSectionBlock({ section, decoratedSteps }: {
  section: LandingSection;
  decoratedSteps: LandingPageConfig['decoratedSteps'];
}) {
  const paragraphs = section.paragraphs || [];
  const list = section.list || [];
  const steps = section.steps || [];
  const ListTag = section.ordered ? 'ol' : 'ul';

  return (
    <Stack gap={4} as="section" wide>
      {section.title ? (
        <Typography as="h2" role="title/section">
          {section.title}
        </Typography>
      ) : null}
      {paragraphs.map((paragraph, index) => (
        <Typography as="p" role="body/base" key={index}>
          {paragraph}
        </Typography>
      ))}
      {steps.length > 0 ? (
        <Columns columns={2} gap={5} wideLast>
          {steps.map((step, index) => (
            <Card
              key={index}
              surface="plain"
              edges={decoratedSteps[index + 1] ? { pieces: decoratedSteps[index + 1]! } : undefined}
              tone={STEP_TONES[index % STEP_TONES.length]}
              step={index + 1}
              title={step.title}
              testId={`landing-step-${index + 1}`}
            >
              {step.text}
            </Card>
          ))}
        </Columns>
      ) : null}
      {list.length > 0 ? (
        <Prose>
          <ListTag>
            {list.map((item, index) => (
              <li key={index}>
                {isListItem(item) ? (
                  <>
                    <strong>{item.title}</strong> {item.text}
                  </>
                ) : (
                  item
                )}
              </li>
            ))}
          </ListTag>
        </Prose>
      ) : null}
    </Stack>
  );
}

export function LandingPage() {
  const premiumInfoHref = usePremiumInfoUrl();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const config = landingConfigFor(pathname);
  const [pastHero, setPastHero] = useState(false);
  const copy = config
    ? (t(`landing.${config.slug}`, { returnObjects: true }) as LandingCopy)
    : null;

  useEffect(() => {
    const node = document.querySelector('[data-testid="landing-create"]');
    const header = document.querySelector('[data-testid="app-header"]');
    if (!(node instanceof HTMLElement)) {
      return;
    }
    const topInset = header instanceof HTMLElement ? header.getBoundingClientRect().height : 0;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setPastHero(!entry.isIntersecting && entry.boundingClientRect.top < topInset);
      },
      { rootMargin: `-${topInset}px 0px 0px 0px` },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [config]);

  if (!config || !copy) {
    return null;
  }

  const [lead, ...rest] = copy.sections;
  const leadParagraphs = !lead?.title && !lead?.steps && !lead?.list ? lead?.paragraphs || [] : null;
  const bodySections = leadParagraphs ? rest : copy.sections;
  const setupIndex = bodySections.findIndex((section) => Boolean(section.steps?.length));
  const introSections = bodySections.slice(0, setupIndex < 0 ? 0 : setupIndex);
  const remainingSections = bodySections.slice(setupIndex < 0 ? 0 : setupIndex);
  const related = EXCHANGE_CARDS.filter((card) => card.href !== config.path);

  return (
    <Shell
      headerCta={
        pastHero ? (
          <Button
            variant="primary"
            size="small"
            testId="header-create"
            onClick={() => navigate(boxLink(config))}
          >
            {copy.buttonTitle}
          </Button>
        ) : null
      }
    >
      <PageContent testId="landing">
        <Stack gap={12}>
          <Stack gap={5} align="center" as="header" wide className="landscape-sky-content" testId="landing-hero">
            <PageHeading title={copy.title} subtitle={copy.subtitle} />
            <LandingCta config={config} label={copy.buttonTitle} testId="landing-create" />
          </Stack>

          <Stack gap={10} wide testId="landing-body">
            <Panel elevated wide edges={{ pieces: 3 }} testId="landing-guide">
              <Stack gap={10} wide>
                {copy.eyebrow || copy.leadTitle || leadParagraphs ? (
                  <Stack gap={4} as="section" wide testId="landing-lead">
                    {copy.eyebrow ? (
                      <Typography as="p" role="title/eyebrow">
                        {copy.eyebrow}
                      </Typography>
                    ) : null}
                    {copy.leadTitle ? (
                      <Typography as="h2" role="title/section">
                        {copy.leadTitle}
                      </Typography>
                    ) : null}
                    {(leadParagraphs || []).map((paragraph, index) => (
                      <Typography as="p" role="body/base" key={index}>
                        {paragraph}
                      </Typography>
                    ))}
                  </Stack>
                ) : null}
    
                {introSections.map((section, index) => (
                  <LandingSectionBlock key={index} section={section} decoratedSteps={config.decoratedSteps} />
                ))}
              </Stack>
            </Panel>

            {remainingSections.map((section, index) => (
              <LandingSectionBlock key={index} section={section} decoratedSteps={config.decoratedSteps} />
            ))}

            <Card surface="plain" edges={{ pieces: 2 }} tone="info" align="center" title={copy.buttonTitle} testId="landing-cta">
              <Stack gap={5} align="center">
                {copy.subtitle}
                <LandingCta config={config} label={copy.buttonTitle} testId="landing-cta-button" />
                {!isDotCom() && <Link href={premiumInfoHref}>{t('footer.premium')}</Link>}
              </Stack>
            </Card>

            {copy.faq && copy.faq.length > 0 ? (
              <Stack gap={5} as="section" wide testId="landing-faq">
                <Typography as="h2" role="title/section">
                  {t('landing.faqTitle')}
                </Typography>
                <ExpandableGroup testId="landing-faq-list">
                  {copy.faq.map((item) => (
                    <Expandable key={item.question} title={item.question}>
                      {item.answer}
                    </Expandable>
                  ))}
                </ExpandableGroup>
              </Stack>
            ) : null}

            {related.length > 0 ? (
              <Stack gap={5} as="section" wide testId="landing-related">
                <Typography as="h2" role="title/section">
                  {t('landing.relatedTitle')}
                </Typography>
                <ExchangeGrid columns={4}>
                  {related.map((card) => (
                    <ExchangeCard
                      key={card.href}
                      href={card.href}
                      image={card.image}
                      icon={<HomeArtwork name={card.icon} />}
                      detailsLabel={t('home.exchanges.details')}
                      tag={t(card.tagKey)}
                      title={t(card.titleKey)}
                      testId={`landing-related-${card.href.split('/').pop()}`}
                    />
                  ))}
                </ExchangeGrid>
              </Stack>
            ) : null}
          </Stack>
        </Stack>
      </PageContent>
    </Shell>
  );
}
